import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Avatar,
    CircularProgress,
} from '@mui/material';
import theme from '../../../../theme';
import { useDeficiencies } from '../DeficiencyContext';

/**
 * Modal for assigning deficiencies to businesses
 * Allows user to select a business from a list for selected deficiencies
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onSubmit - Callback when assignment is submitted with the business name
 * @returns {React.ReactElement} Business selection modal
 */
const AssigneeModal = ({ open, onClose, onSubmit }) => {
    // Get businesses from context
    const { state } = useDeficiencies();
    const { businesses, businessesLoading } = state;

    // Track the selected business
    const [selectedBusiness, setSelectedBusiness] = useState(null);

    /**
     * Handle submission of the assignment
     * Calls onSubmit with the selected business object containing name and businessId
     */
    const handleSubmit = () => {
        if (selectedBusiness) {
            // Pass the business name and businessId to the onSubmit function
            onSubmit({
                assignee: selectedBusiness.businessName,
                businessId: selectedBusiness.businessId,
            });

            // Close the modal after submitting
            onClose();
        }
    };

    /**
     * Get business type display text for subtitle
     */
    const getBusinessTypeDisplay = (business) => {
        // Use the new BusinessDTO structure
        if (business.subscriptionType !== null) {
            return 'Internal';
        } else {
            return 'Subcontractor';
        }
    };

    /**
     * Get initials for business name
     */
    const getBusinessInitials = (businessName) => {
        // Add null/undefined check
        if (!businessName || typeof businessName !== 'string') {
            return 'N/A'; // Default fallback for missing names
        }

        const words = businessName.split(' ');
        if (words.length >= 2) {
            return words[0][0] + words[1][0];
        }
        return businessName.substring(0, 2);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '100%',
                    maxWidth: '400px',
                },
            }}
        >
            <DialogTitle>
                <Typography variant="h6" fontWeight={600}>
                    Assign to Business
                </Typography>
            </DialogTitle>

            <DialogContent>
                {businessesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {businesses
                            .filter((business) => business && business.businessId)
                            .map((business) => {
                                return (
                                    <Box
                                        key={business.businessId}
                                        onClick={() => setSelectedBusiness(business)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            p: 2,
                                            borderRadius: 2,
                                            cursor: 'pointer',
                                            border: '1px solid',
                                            borderColor:
                                                selectedBusiness?.businessId === business.businessId
                                                    ? theme.palette.primary.main
                                                    : theme.palette.grey[100],
                                            backgroundColor:
                                                selectedBusiness?.businessId === business.businessId
                                                    ? theme.palette.grey[50]
                                                    : 'transparent',
                                            '&:hover': {
                                                backgroundColor:
                                                    selectedBusiness?.businessId ===
                                                    business.businessId
                                                        ? theme.palette.grey[50]
                                                        : theme.palette.grey[50],
                                            },
                                        }}
                                    >
                                        {/* Business avatar with initials */}
                                        <Avatar
                                            sx={{
                                                bgcolor:
                                                    selectedBusiness?.businessId ===
                                                    business.businessId
                                                        ? theme.palette.primary.main
                                                        : theme.palette.grey[200],
                                                width: 40,
                                                height: 40,
                                            }}
                                        >
                                            {getBusinessInitials(business.businessName)}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body1" fontWeight={500}>
                                                {business.businessName || 'Unnamed Business'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {getBusinessTypeDisplay(business)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="outlined" color="inherit">
                    Cancel
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={!selectedBusiness}>
                    Assign
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssigneeModal;
